import { ratio, type Ratio } from '@radish/primitives';

/**
 * Rounding modes (core plan §4.2, pricing-pipeline §0). Every rounding call names its mode; there is no default.
 *
 * | mode      | rule                            | 2.5 | -2.5 | 2.4 | -2.4 | 2.6 | -2.6 |
 * |-----------|---------------------------------|-----|------|-----|------|-----|------|
 * | HALF_UP   | nearest, ties toward +infinity  |  3  |  -2  |  2  |  -2  |  3  |  -3  |
 * | HALF_AWAY | nearest, ties away from zero    |  3  |  -3  |  2  |  -2  |  3  |  -3  |
 * | FLOOR     | toward -infinity                |  2  |  -3  |  2  |  -3  |  2  |  -3  |
 * | CEIL      | toward +infinity                |  3  |  -2  |  3  |  -2  |  3  |  -2  |
 * | DOWN      | toward zero (truncate)          |  2  |  -2  |  2  |  -2  |  2  |  -2  |
 *
 * HALF_UP matches legacy Dinero HALF_UP and JS Math.round; HALF_AWAY matches PHP round(). They differ only on
 * negative halves, and core rounds non-negative quantities at every pricing step, so both agree there.
 */
export type Rounding = 'HALF_UP' | 'HALF_AWAY' | 'FLOOR' | 'CEIL' | 'DOWN';

export const ROUNDING_MODES: readonly Rounding[] = ['HALF_UP', 'HALF_AWAY', 'FLOOR', 'CEIL', 'DOWN'];

/** Floor division for a positive divisor (bigint `/` truncates toward zero). */
export const floorDiv = (num: bigint, den: bigint): bigint => {
  const q = num / den;
  return num % den !== 0n && num < 0n ? q - 1n : q;
};

/** Remainder of floorDiv: always in [0, den) for a positive divisor. */
export const floorMod = (num: bigint, den: bigint): bigint => num - floorDiv(num, den) * den;

const halfUp = (num: bigint, den: bigint): bigint => floorDiv(2n * num + den, 2n * den);

/** Rounds an exact rational to an integer with the named mode. */
export const round = (r: Ratio, mode: Rounding): bigint => {
  const { num, den } = r.den > 0n ? r : ratio(r.num, r.den);
  switch (mode) {
    case 'FLOOR':
      return floorDiv(num, den);
    case 'CEIL':
      return -floorDiv(-num, den);
    case 'DOWN':
      return num / den;
    case 'HALF_UP':
      return halfUp(num, den);
    case 'HALF_AWAY':
      return num < 0n ? -halfUp(-num, den) : halfUp(num, den);
  }
};
